import { useState } from "react";
import { companionDownlink, delayCodeGroups, delayCodesInGroup } from "@northstar/dispatch-core";
import type { PilotBoard } from "../pilotBoard";

const ATA_CHAPTERS = [
  ["21", "Air conditioning"],
  ["22", "Auto flight"],
  ["23", "Communications"],
  ["24", "Electrical power"],
  ["27", "Flight controls"],
  ["28", "Fuel"],
  ["29", "Hydraulic power"],
  ["30", "Ice and rain protection"],
  ["32", "Landing gear"],
  ["34", "Navigation"],
  ["36", "Pneumatic"],
  ["49", "APU"],
  ["71", "Powerplant"],
  ["72", "Engine"],
  ["73", "Engine fuel and control"],
  ["79", "Oil"],
  ["80", "Starting"],
] as const;

function stationIcao(route: string): string {
  const token = route.split(/[–—-]/)[0]?.trim().toUpperCase() ?? "";
  return /^[A-Z0-9]{4}$/.test(token) ? token : "";
}

export function DispatchInbox({
  board,
  unread = 0,
  onMarkRead,
  onReply,
}: {
  board: PilotBoard;
  unread?: number;
  onMarkRead?: () => void;
  onReply?: (packet: string) => Promise<{ ok: true } | { ok: false; error: string }> | { ok: true } | { ok: false; error: string };
}) {
  const [kind, setKind] = useState<"delay" | "mx" | "fuel" | "free_text">("delay");
  const [code, setCode] = useState("93");
  const [minutes, setMinutes] = useState("15");
  const [etd, setEtd] = useState("");
  const [remarks, setRemarks] = useState("");
  const [ata, setAta] = useState("28");
  const [text, setText] = useState("");
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState<"lb" | "kg" | "L" | "USG">("lb");
  const [airport, setAirport] = useState(() => stationIcao(board.route));
  const [local, setLocal] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [sent, setSent] = useState("");
  const hoppie = board.acarsHoppie;

  return (
    <article className="panel" data-testid="dispatch-inbox">
      <p className="kicker">Dispatch</p>
      <h2>
        Messages
        {unread > 0 ? (
          <span data-testid="acars-unread"> {unread} unread</span>
        ) : null}
      </h2>
      {board.messages.length || local.length ? (
        <ul className="pilot-list" data-testid="dispatch-messages">
          {board.messages.map((row) => (
            <li key={row.id}>{row.text}</li>
          ))}
          {local.map((row) => (
            <li key={row}>{row}</li>
          ))}
        </ul>
      ) : (
        <p className="muted">No dispatch messages on this trip.</p>
      )}
      {unread > 0 && onMarkRead ? (
        <button type="button" className="btn ghost" data-testid="acars-mark-read" onClick={onMarkRead}>
          Mark read
        </button>
      ) : null}
      {hoppie ? (
        <p className="muted" data-testid="acars-hoppie-readonly">
          This tail uses aircraft ACARS. The lines above are a copy. Send the reply from the cockpit.
        </p>
      ) : (
        <form
          data-testid="companion-reply"
          onSubmit={(event) => {
            event.preventDefault();
            const drafted = companionDownlink(
              kind === "delay"
                ? { kind, code, minutes: Number(minutes), etd, remarks }
                : kind === "mx"
                  ? { kind, ata, text }
                  : kind === "fuel"
                    ? { kind, amount: Number(amount), unit, airport }
                    : { kind, text },
            );
            if (!drafted.ok) {
              setError(drafted.error);
              setSent("");
              return;
            }
            setError("");
            if (!onReply) {
              setLocal((rows) => [...rows, drafted.packet]);
              setSent("");
              return;
            }
            void Promise.resolve(onReply(drafted.packet)).then((result) => {
              if (!result.ok) {
                setError(result.error);
                setSent("");
                return;
              }
              setSent("Queued for the desk.");
            });
          }}
        >
          <label>
            Reply
            <select data-testid="companion-reply-kind" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="delay">Delay</option>
              <option value="mx">Mx write-up</option>
              <option value="fuel">Fuel uplift</option>
              <option value="free_text">Free text</option>
            </select>
          </label>
          {kind === "delay" ? (
            <>
              <label>
                Delay code
                <select data-testid="companion-delay-code" value={code} onChange={(e) => setCode(e.target.value)}>
                  {delayCodeGroups().map((group) => (
                    <optgroup key={group} label={group}>
                      {delayCodesInGroup(group).map((row) => (
                        <option key={row.code} value={row.code}>
                          {row.code} {row.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
              <label>
                Minutes
                <input data-testid="companion-delay-min" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
              </label>
              <label>
                New ETD
                <input data-testid="companion-delay-etd" inputMode="numeric" placeholder="HHMM" value={etd} onChange={(e) => setEtd(e.target.value)} />
              </label>
              <label>
                Remarks
                <input data-testid="companion-delay-rmk" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
              </label>
            </>
          ) : null}
          {kind === "mx" ? (
            <>
              <label>
                ATA chapter
                <select data-testid="companion-ata" value={ata} onChange={(e) => setAta(e.target.value)}>
                  {ATA_CHAPTERS.map(([chapter, label]) => (
                    <option key={chapter} value={chapter}>
                      {chapter} {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Defect
                <input data-testid="companion-reply-text" value={text} onChange={(e) => setText(e.target.value)} />
              </label>
            </>
          ) : null}
          {kind === "fuel" ? (
            <>
              <label>
                Quantity
                <input data-testid="companion-fuel-qty" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </label>
              <label>
                Unit
                <select data-testid="companion-fuel-unit" value={unit} onChange={(e) => setUnit(e.target.value as typeof unit)}>
                  <option value="lb">lb</option>
                  <option value="kg">kg</option>
                  <option value="L">L</option>
                  <option value="USG">USG</option>
                </select>
              </label>
              <label>
                Airport
                <input data-testid="companion-fuel-icao" value={airport} onChange={(e) => setAirport(e.target.value.toUpperCase())} />
              </label>
            </>
          ) : null}
          {kind === "free_text" ? (
            <label>
              Message
              <input data-testid="companion-reply-text" value={text} onChange={(e) => setText(e.target.value)} />
            </label>
          ) : null}
          {error ? <p className="err">{error}</p> : null}
          {sent ? <p className="muted">{sent}</p> : null}
          <button type="submit" className="btn" data-testid="companion-reply-send">
            Queue reply
          </button>
        </form>
      )}
    </article>
  );
}
